import React, { useEffect, useState, useCallback } from "react";
import { useNotify, useDataProvider } from "react-admin";
import { useClub } from "../club/ClubContext";
import { httpClient } from "../httpClient";
import { PlayerPenalty, PenaltyType } from "./types";
import {
  Box, Typography, Card, CardContent, Chip, IconButton, Tooltip,
  Button, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, Autocomplete, Avatar, Divider, CircularProgress, Slider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

function formatPrice(n: number) {
  return `€ ${n.toFixed(2).replace(".", ",")}`;
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ---------------------------------------------------------------------------
// Assign Dialog
// ---------------------------------------------------------------------------
function AssignDialog({ open, onClose, clubId, penaltyTypes, onAssigned }: {
  open: boolean;
  onClose: () => void;
  clubId: number;
  penaltyTypes: PenaltyType[];
  onAssigned: () => void;
}) {
  const notify = useNotify();
  const [players, setPlayers] = useState<{ id: number; display_name: string; email: string; avatar_url?: string }[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<string>("");
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedTypeObj = penaltyTypes.find(t => String(t.id) === selectedType);
  const hasRange =
    selectedTypeObj?.min_value != null && selectedTypeObj?.max_value != null && selectedTypeObj?.delta != null;

  useEffect(() => {
    if (!open) return;
    httpClient(`/api/penalties/eligible-players?club_id=${clubId}`)
      .then(({ json }) => setPlayers(json?.data ?? []))
      .catch(() => setPlayers([]));
  }, [open, clubId]);

  useEffect(() => {
    if (hasRange && selectedTypeObj) {
      setSliderValue(selectedTypeObj.min_value as number);
    }
  }, [selectedType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!selectedPlayer || !selectedType) return;
    if (hasRange && (sliderValue < (selectedTypeObj!.min_value as number) || sliderValue > (selectedTypeObj!.max_value as number))) return;
    setSaving(true);
    try {
      await httpClient("/api/penalties", {
        method: "POST",
        body: JSON.stringify({
          club_id: clubId,
          player_id: selectedPlayer.id,
          penalty_type_id: Number(selectedType),
          note: note || undefined,
          amount_override: hasRange ? sliderValue : undefined,
        }),
      });
      notify("Strafe vergeben");
      setSelectedPlayer(null);
      setSelectedType("");
      setNote("");
      onClose();
      onAssigned();
    } catch (e: any) {
      notify(e?.body?.detail ?? "Fehler beim Vergeben", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Strafe vergeben</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
        <Autocomplete
          options={players}
          getOptionLabel={(o) => `${o.display_name} (${o.email})`}
          value={selectedPlayer}
          onChange={(_, v) => setSelectedPlayer(v)}
          renderOption={(props, o) => (
            <Box component="li" {...props} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Avatar src={o.avatar_url} sx={{ width: 28, height: 28, fontSize: 12 }}>{o.display_name?.[0]}</Avatar>
              <Box><Typography variant="body2">{o.display_name}</Typography><Typography variant="caption" color="text.secondary">{o.email}</Typography></Box>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Spieler" />}
        />
        <FormControl fullWidth>
          <InputLabel>Strafentyp</InputLabel>
          <Select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} label="Strafentyp">
            {penaltyTypes.filter(t => t.active).map(t => (
              <MenuItem key={t.id} value={String(t.id)}>
                {t.name} –{" "}
                {t.min_value != null && t.max_value != null && t.delta != null
                  ? `${formatPrice(t.min_value)} – ${formatPrice(t.max_value)}`
                  : formatPrice(t.amount)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasRange && selectedTypeObj && (
          <Box sx={{ px: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Betrag: <strong>{formatPrice(sliderValue)}</strong>
            </Typography>
            <Slider
              value={sliderValue}
              onChange={(_, v) => setSliderValue(v as number)}
              min={selectedTypeObj.min_value as number}
              max={selectedTypeObj.max_value as number}
              step={selectedTypeObj.delta as number}
              valueLabelDisplay="auto"
              valueLabelFormat={formatPrice}
              marks={[
                { value: selectedTypeObj.min_value as number, label: formatPrice(selectedTypeObj.min_value as number) },
                { value: selectedTypeObj.max_value as number, label: formatPrice(selectedTypeObj.max_value as number) },
              ]}
            />
          </Box>
        )}

        <TextField label="Anmerkung (optional)" value={note} onChange={e => setNote(e.target.value)} multiline rows={2} fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!selectedPlayer || !selectedType || saving}>
          {saving ? <CircularProgress size={18} /> : "Vergeben"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete Dialog
// ---------------------------------------------------------------------------
function DeleteDialog({ penalty, onClose, onDeleted }: {
  penalty: PlayerPenalty | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const notify = useNotify();
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (!penalty) setReason(""); }, [penalty]);

  const handleDelete = async () => {
    if (!penalty) return;
    setDeleting(true);
    try {
      await httpClient(`/api/penalties/${penalty.id}`, {
        method: "DELETE",
        body: JSON.stringify({ reason: reason || undefined }),
      });
      notify("Strafe gelöscht");
      onClose();
      onDeleted();
    } catch {
      notify("Fehler beim Löschen", { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!penalty} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Strafe zurückziehen?</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
        <DialogContentText>
          Strafe <strong>{penalty?.penalty_type_name}</strong> ({penalty ? formatPrice(penalty.amount) : ""}) für{" "}
          <strong>{penalty?.player_display_name}</strong> zurückziehen?
          Der Spieler erhält eine Benachrichtigung.
        </DialogContentText>
        <TextField
          label="Grund (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
          multiline rows={2}
          fullWidth
          helperText="Wird dem Spieler in der Nachricht angezeigt"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button color="error" onClick={handleDelete} disabled={deleting}>
          {deleting ? <CircularProgress size={18} /> : "Zurückziehen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function PenaltyList() {
  const { clubId } = useClub();
  const notify = useNotify();
  const [penalties, setPenalties] = useState<PlayerPenalty[]>([]);
  const [penaltyTypes, setPenaltyTypes] = useState<PenaltyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PlayerPenalty | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const load = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const [penRes, typRes] = await Promise.all([
        httpClient(`/api/penalties?club_id=${clubId}&perPage=200`).then(r => r.json?.data ?? []),
        httpClient(`/api/penalties/types?club_id=${clubId}`).then(r => r.json?.data ?? []),
      ]);
      setPenalties(penRes);
      setPenaltyTypes(typRes);
    } catch {
      notify("Fehler beim Laden", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [clubId, notify]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (p: PlayerPenalty) => {
    const newStatus = p.status === "open" ? "paid" : "open";
    try {
      await httpClient(`/api/penalties/${p.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setPenalties(prev => prev.map(x => x.id === p.id ? { ...x, status: newStatus as "open" | "paid" } : x));
    } catch {
      notify("Fehler beim Aktualisieren", { type: "error" });
    }
  };

  const filtered = statusFilter === "all" ? penalties : penalties.filter(p => p.status === statusFilter);
  const totalOpen = penalties.filter(p => p.status === "open").reduce((s, p) => s + p.amount, 0);

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Strafen</Typography>
          {totalOpen > 0 && (
            <Typography variant="body2" color="warning.main">
              Offen gesamt: {formatPrice(totalOpen)}
            </Typography>
          )}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssignOpen(true)}>
          Strafe vergeben
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        {["all", "open", "paid"].map(s => (
          <Chip
            key={s}
            label={s === "all" ? "Alle" : s === "open" ? "Offen" : "Bezahlt"}
            color={statusFilter === s ? "primary" : "default"}
            onClick={() => setStatusFilter(s)}
            variant={statusFilter === s ? "filled" : "outlined"}
          />
        ))}
      </Box>

      {filtered.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" mt={4}>Keine Strafen vorhanden.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {filtered.map(p => (
            <Card key={p.id} sx={{ borderLeft: "4px solid", borderLeftColor: p.status === "paid" ? "success.main" : "warning.main" }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar src={p.player_avatar_url} sx={{ width: 36, height: 36, fontSize: 14 }}>
                      {p.player_display_name?.[0]}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600}>{p.player_display_name}</Typography>
                      <Typography variant="body2" color="text.secondary">{p.player_email}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={p.status === "paid" ? "Bezahlt" : "Offen"}
                      color={p.status === "paid" ? "success" : "warning"}
                      size="small"
                    />
                    <Tooltip title={p.status === "paid" ? "Als offen markieren" : "Als bezahlt markieren"}>
                      <IconButton size="small" onClick={() => toggleStatus(p)}>
                        {p.status === "paid"
                          ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                          : <RadioButtonUncheckedIcon color="warning" fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Strafe zurückziehen">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(p)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{p.penalty_type_name}</Typography>
                    {p.note && <Typography variant="body2" color="text.secondary">Anmerkung: {p.note}</Typography>}
                    <Typography variant="caption" color="text.secondary">
                      von {p.assigned_by_display_name ?? "–"} · {formatDate(p.created_at)}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} color={p.status === "paid" ? "success.main" : "warning.main"}>
                    {formatPrice(p.amount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        clubId={clubId!}
        penaltyTypes={penaltyTypes}
        onAssigned={load}
      />
      <DeleteDialog
        penalty={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); load(); }}
      />
    </Box>
  );
}
